import { storableError } from '../../util/errors';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { denormalisedResponseEntities } from '../../util/data';
import { transactionsToEvents, reservationsToEvents } from './CalendarPage.helpers';
import { episodeRsvpAPI } from '../../api/clients/episodeRsvpAPI';
import { episodeKeyOf, resolveEpisode } from '../../util/episodes';

// ================ Action types ================ //

export const QUERY_EVENTS_REQUEST = 'app/CalendarPage/QUERY_EVENTS_REQUEST';
export const QUERY_EVENTS_SUCCESS = 'app/CalendarPage/QUERY_EVENTS_SUCCESS';
export const QUERY_EVENTS_ERROR = 'app/CalendarPage/QUERY_EVENTS_ERROR';

// ================ Reducer ================ //

const initialState = {
  queryEventsInProgress: false,
  queryEventsError: null,
  events: [],
  transactionRefs: [],
  // Live Rooms of episodes reserved without a booking (the episode card)
  reservationListingRefs: [],
};

const entityRefs = entities =>
  entities.map(entity => ({
    id: entity.id,
    type: entity.type,
  }));

const resultIds = data => data.data.map(l => l.id);

const calendarPageReducer = (state = initialState, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case QUERY_EVENTS_REQUEST:
      return {
        ...state,
        queryEventsInProgress: true,
        queryEventsError: null,
      };
    case QUERY_EVENTS_SUCCESS:
      return {
        ...state,
        events: payload.events,
        transactionRefs: entityRefs(payload.data.data),
        reservationListingRefs: payload.reservationListingRefs || [],
        queryEventsInProgress: false,
      };
    case QUERY_EVENTS_ERROR:
      // eslint-disable-next-line no-console
      console.error(payload);
      return { ...state, queryEventsInProgress: false, queryEventsError: payload };

    default:
      return state;
  }
};

export default calendarPageReducer;

// ================ Action creators ================ //

export const queryEventsRequest = () => ({
  type: QUERY_EVENTS_REQUEST,
});

export const queryEventsSuccess = (response, events, reservationListingRefs) => ({
  type: QUERY_EVENTS_SUCCESS,
  payload: { data: response.data, events, reservationListingRefs },
});

export const queryEventsError = e => ({
  type: QUERY_EVENTS_ERROR,
  error: true,
  payload: e,
});

// ================ Thunks ================ //

export const queryEvents = () => (dispatch, getState, sdk) => {
  dispatch(queryEventsRequest());

  return sdk.transactions
    .query({
      only: 'order',
      lastTransitions: ['transition/request-booking', 'transition/request-booking-after-enquiry'],
      processNames: ['flex-booking-process'],
      include: ['listing', 'listing.images', 'booking'],
      'fields.transaction': ['metadata'],
      'fields.image': ['variants.landscape-crop', 'variants.landscape-crop2x'],
      'limit.images': 1,
    })
    .then(async response => {
      const denormalisedEntities = denormalisedResponseEntities(response);

      const events = transactionsToEvents(denormalisedEntities);
      dispatch(addMarketplaceEntities(response));

      // Spots reserved from the episode card have no booking, so they come
      // from the member's own reservation list. An episode that also has a
      // booking is shown once, as the booking.
      const bookedEpisodeIds = new Set(
        denormalisedEntities
          .map(tx => {
            const slotId = tx?.attributes?.metadata?.timeslot?.id;
            return episodeKeyOf(resolveEpisode(tx.listing, slotId)) || slotId;
          })
          .filter(Boolean)
      );
      let reservationEvents = [];
      let reservationListingRefs = [];
      try {
        const res = await episodeRsvpAPI.mine();
        const body = res?.data ?? res;
        const reserved = (body?.data?.episodes || []).filter(
          e => !bookedEpisodeIds.has(e.episodeId)
        );
        const listingIds = [...new Set(reserved.map(e => e.listingId))];
        if (listingIds.length > 0) {
          const listingsResponse = await sdk.listings.query({
            ids: listingIds,
            include: ['images'],
            'fields.image': ['variants.landscape-crop', 'variants.landscape-crop2x'],
            'limit.images': 1,
          });
          dispatch(addMarketplaceEntities(listingsResponse));
          reservationListingRefs = entityRefs(listingsResponse.data.data);
        }
        reservationEvents = reservationsToEvents(reserved);
      } catch (e) {
        reservationEvents = [];
      }

      dispatch(
        queryEventsSuccess(response, [...events, ...reservationEvents], reservationListingRefs)
      );
      return response;
    })
    .catch(e => dispatch(queryEventsError(storableError(e))));
};

export const loadData = () => (dispatch, getState, sdk) => {
  return dispatch(queryEvents());
};
